import React from 'react';

import { Box, Button, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadSharpIcon from '@mui/icons-material/FileDownloadSharp';

import {
  COLOR_GREY_600,
  COLOR_PRIMARY_2,
  COLOR_TEXT_4,
  SIDEBAR_COLOR,
  SUBSCRIPTION_MODAL_DARK_THEME_COLOR,
} from 'src/colors';
import { exchangeRates, PRICING_TABLE_TOOLTIPS } from 'src/const';
import {
  calculateCouponChange,
  checkPromocodeValidity,
  formatCurrencyBasedOnCountryCode,
  isCurrentPlanSubscribed,
} from 'src/utils/functions';
import { InfoButton } from 'src/App/Components/Buttons';
import InfoTooltipContent from 'src/App/HOC/Tooltips/InfoTooltipContent';

const SubscriptionTierCard = ({
  plan,
  title,
  description,
  uniqueFeatures,
  uniqueFeaturesTooltip,
  prevTier,
  userLocalCurrency,
  user,
  duration,
  subscriptionPriceAccordingToUserLocalCurrency,
  promoCodeDiscountsDict,
  product,
  setProduct,
  promoCode,
  isPromoCodeValid,
  openDemoBookingForm,
  paymentModalRef,
  setFormHeadingFor,
  setShowUpgradeSubscriptionModal,
}) => {
  const currentActivePlan = user.subscriptionPlan;

  let price: any = 0;
  let priceDiscountedMonthly: any = 0;
  let priceDiscountedYearly: any = 0;

  let isPlanSubscribed = isCurrentPlanSubscribed(plan, currentActivePlan);

  if (plan != 'starter') {
    price =
      duration == 'monthly'
        ? formatCurrencyBasedOnCountryCode(
            parseFloat(subscriptionPriceAccordingToUserLocalCurrency[plan]['monthly'].toFixed(2)),
            userLocalCurrency
          )
        : formatCurrencyBasedOnCountryCode(
            parseFloat(
              (subscriptionPriceAccordingToUserLocalCurrency[plan][duration] / 12).toFixed()
            ),
            userLocalCurrency
          );

    if (isPromoCodeValid) {
      let dPrice: any = calculateCouponChange(
        promoCode,
        promoCodeDiscountsDict,
        subscriptionPriceAccordingToUserLocalCurrency,
        duration,
        () => {},
        plan
      );

      priceDiscountedMonthly = formatCurrencyBasedOnCountryCode(
        parseFloat((dPrice / 12).toFixed(2)),
        userLocalCurrency
      );
      priceDiscountedYearly = formatCurrencyBasedOnCountryCode(dPrice, userLocalCurrency);
    }
  }

  const handleBuyNowBtnclick = chosenPlan => {
    const easeInOutQuad = t => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

    if (chosenPlan == 'starter') {
      return;
    }

    setProduct(chosenPlan);

    if (
      (['expert', 'scale'].includes(chosenPlan) && currentActivePlan == 'pro') ||
      (chosenPlan == 'scale' && currentActivePlan == 'expert')
    ) {
      setShowUpgradeSubscriptionModal(chosenPlan);
      return;
    }

    if (chosenPlan != 'starter') {
      if (paymentModalRef.current) {
        const modal = paymentModalRef.current;
        const scrollHeight = modal.scrollHeight;
        const duration = 500; // Adjust the duration as needed (in milliseconds)
        const startTime = performance.now();
        const startScroll = modal.scrollTop;

        const targetScroll = scrollHeight - modal.clientHeight - 500;

        const scrollStep = () => {
          const now = performance.now();
          const time = Math.min(1, (now - startTime) / duration);
          modal.scrollTop = startScroll + (targetScroll - startScroll) * easeInOutQuad(time);

          if (time < 1) {
            requestAnimationFrame(scrollStep);
          }
        };

        requestAnimationFrame(scrollStep);
      }
    }
  };

  return (
    <Box
      sx={{
        background: 'white',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.25)',
        borderRadius: 5,
        pt: 5,
        pb: 5,
        pl: 4,
        pr: 4,
      }}
    >
      {plan == 'expert' && (
        <Box
          sx={{
            backgroundColor: SUBSCRIPTION_MODAL_DARK_THEME_COLOR,
            padding: '5px 10px',
            mb: 2,
            borderRadius: 10,
            width: 'fit-content',
          }}
        >
          <Typography style={{ fontSize: '12px', fontWeight: 500, color: SIDEBAR_COLOR }}>
            Popular
          </Typography>
        </Box>
      )}

      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
        {title.split(' ').length > 2 ? (
          <>
            {title.split(' ')[0]} {title.split(' ')[1]}{' '}
            <span style={{ color: COLOR_PRIMARY_2 }}>{title.split(' ')[2]}</span>
          </>
        ) : (
          <>
            {title.split(' ')[0]}{' '}
            <span style={{ color: COLOR_PRIMARY_2 }}>{title.split(' ')[1]}</span>
          </>
        )}
      </Typography>

      <Typography variant="body1" sx={{ mt: 3, color: COLOR_TEXT_4 }}>
        {description}
      </Typography>

      <Box sx={{ mt: 3 }}>
        {/* Original pricing */}
        <Typography
          variant={isPromoCodeValid && plan !== 'starter' ? 'h6' : 'h4'}
          sx={{
            fontWeight: 'bold',
            textDecoration: isPromoCodeValid && plan !== 'starter' ? 'line-through' : 'none',
            display: 'inline',
          }}
        >
          {exchangeRates[userLocalCurrency].symbol}
          {price}
        </Typography>

        {/* Promo code discounted pricing */}
        {isPromoCodeValid && plan !== 'starter' && (
          <Typography
            variant="h4"
            style={{
              fontWeight: 'bold',
              marginLeft: '5px',
              display: 'inline',
            }}
          >
            {exchangeRates[userLocalCurrency].symbol}
            {duration == 'yearly' ? priceDiscountedMonthly : priceDiscountedYearly}
          </Typography>
        )}
        <Typography variant="body1" sx={{ color: COLOR_GREY_600 }}>
          /month
        </Typography>

        {/* Billed annually section */}
        {duration == 'yearly' && plan !== 'starter' && (
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              mt: 1,
            }}
          >
            Billed Annually: {exchangeRates[userLocalCurrency].symbol}
            {isPromoCodeValid
              ? priceDiscountedYearly
              : formatCurrencyBasedOnCountryCode(
                  parseFloat(
                    subscriptionPriceAccordingToUserLocalCurrency[plan]['yearly'].toFixed(2)
                  ),
                  userLocalCurrency
                )}
          </Typography>
        )}
      </Box>

      <Button
        fullWidth
        size="small"
        sx={{
          border: isPlanSubscribed ? `1px solid ${COLOR_PRIMARY_2}` : 'none',
          background: isPlanSubscribed ? '#ffffff' : COLOR_PRIMARY_2,
          color: isPlanSubscribed ? COLOR_PRIMARY_2 : '#ffffff',
          '&:hover': {
            background: isPlanSubscribed ? '#ffffff' : COLOR_PRIMARY_2,
          },
          mt: 3,
        }}
        onClick={() => handleBuyNowBtnclick(plan)}
      >
        <Typography
          variant="h6"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textTransform: 'capitalize',
          }}
        >
          {isPlanSubscribed ? (
            <>
              <CheckCircleIcon sx={{ marginRight: '5px' }} /> Your Current Plan
            </>
          ) : plan === 'Insight Starter' ? (
            'Try for free'
          ) : (currentActivePlan === 'pro' && (plan === 'expert' || plan === 'scale')) ||
            (currentActivePlan === 'expert' && plan === 'scale') ? (
            'Upgrade'
          ) : (
            'Buy Now'
          )}
        </Typography>
      </Button>

      <Typography variant="body2" sx={{ fontWeight: 500, mt: 3, mb: 1 }}>
        {plan === 'starter' ? 'Key Features:' : `Everything in ${prevTier}, plus:`}
      </Typography>
      <List disablePadding={true} dense={true}>
        {uniqueFeatures.map((feature, index) => (
          <ListItem key={index} disableGutters={true} dense={true}>
            <ListItemIcon sx={{ minWidth: '30px', marginRight: '0px' }}>
              <DoneIcon sx={{ color: 'green' }} />
            </ListItemIcon>
            {feature.includes('Money') ? (
              <ListItemText primary={<Typography sx={{ fontWeight: 500 }}>{feature}</Typography>} />
            ) : feature.includes('Download') ? (
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    {feature.replace('Download', '')}
                    <FileDownloadSharpIcon />
                  </Typography>
                }
              />
            ) : (
              <ListItemText primary={feature} />
            )}
            {uniqueFeaturesTooltip[index] && (
              <InfoButton
                tooltipContent={
                  <InfoTooltipContent
                    content={PRICING_TABLE_TOOLTIPS[uniqueFeaturesTooltip[index]]?.content}
                  />
                }
              />
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default SubscriptionTierCard;
